import { FC, useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button, Input } from "../../../../shared/ui";
import { useHoldUpdateWhileDirty } from "../../../../shared/lib/app-version";

interface Props {
  comment: string;
  isSaving: boolean;
  error: string | null;
  /** Resolves true when stored. An empty string clears the comment. */
  onSave: (text: string) => Promise<boolean>;
}

const MAX_LENGTH = 2000;
/** How long „Gespeichert" stays up. */
const SAVED_FLASH_MS = 2500;

/**
 * The Auftrag's one contact comment, saved on its own button — never on blur, so a half-typed note
 * does not go out. Same draft/dirty rule as `TechnicianFeedbackCard`: a background refetch is
 * adopted unless the office is mid-edit, whose typing wins.
 */
export const ContactCommentField: FC<Props> = ({ comment, isSaving, error, onSave }) => {
  const [draft, setDraft] = useState(comment);
  const [savedFlash, setSavedFlash] = useState(false);

  const dirty = draft.trim() !== comment.trim();
  useHoldUpdateWhileDirty(dirty);

  useEffect(() => {
    if (!dirty) setDraft(comment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment]);

  useEffect(() => {
    if (!savedFlash) return;
    const timer = window.setTimeout(() => setSavedFlash(false), SAVED_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [savedFlash]);

  const save = async () => {
    if (await onSave(draft)) setSavedFlash(true);
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor="contact-comment" className="text-sm font-medium text-slate-700">
        Kommentar
      </label>
      <Input
        id="contact-comment"
        as="textarea"
        value={draft}
        onChange={setDraft}
        maxLength={MAX_LENGTH}
        placeholder="Was wurde versucht, was hat der Mieter gesagt, wann erneut probieren?"
        inputClassName="!min-h-[96px] md:!min-h-[128px]"
        disabled={isSaving}
      />
      <div className="flex justify-between gap-3 text-[13px] text-slate-600">
        <span>Sichtbar für das Büro, nicht für den Mieter.</span>
        <span className="hidden tabular-nums sm:inline">
          {draft.length} / {MAX_LENGTH}
        </span>
      </div>
      <div className="flex items-center justify-end gap-3" aria-live="polite">
        {error ? (
          <span role="alert" className="text-sm text-red-600">
            {error}
          </span>
        ) : savedFlash && !dirty ? (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
            <Check size={14} aria-hidden="true" /> Gespeichert
          </span>
        ) : null}
        <Button size="sm" fullWidth={false} onClick={save} isLoading={isSaving} disabled={!dirty} className="!text-[17px]">
          Kommentar speichern
        </Button>
      </div>
    </div>
  );
};
